import {
  TaskStatus,
  TaskStatusChangeAction,
  SearchableTask,
  isAllowed,
  ResourceType,
  Feature,
  Action,
} from '@super-software-inc/foundation'
import {
  DropdownTriggerButton,
  FlexRow,
  FormDropdown,
  MultilevelDropdown,
  MultilevelItem,
  MultilevelNoResults,
} from 'components/lib'
import { MultilevelHeader, Positions } from 'components/lib/MultilevelDropdown'
import { DocumentData } from 'firebase/firestore'
import React, { useMemo, useState } from 'react'
import { useRecoilValue } from 'recoil'
import { authenticatedUserAtom } from 'state/atoms'
import styled from 'styled-components/macro'
import Status from './Status'

const NullButton = styled.button<{ buttonLike?: boolean }>`
  background: ${props =>
    props.buttonLike ? props.theme.colors.bg200 : 'transparent'};
  margin: 0;
  border: none;
  border-radius: 4px;
  color: ${props => props.theme.colors.text0};
  font-size: 0.8rem;
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  cursor: ${props => (props.buttonLike ? 'pointer' : 'initial')};
  transition: all ${props => props.theme.transitions.short} ease;
  &:hover {
    background: ${props =>
      props.buttonLike ? props.theme.colors.bg300 : 'transparent'};
  }

  &:disabled {
    color: ${props => props.theme.colors.text300};
    cursor: not-allowed;

    &:hover {
      cursor: not-allowed;
    }
  }
`

export type StatusSelectorOnChange = ({
  task,
  newStatus,
  action,
}: {
  task: SearchableTask
  newStatus: TaskStatus
  action: TaskStatusChangeAction
}) => void

interface StatusOption {
  id: number
  value: TaskStatus
  text: string
  verb: string
}

export const statusOptions: StatusOption[] = [
  {
    id: 0,
    value: TaskStatus.OPEN,
    text: 'Open',
    verb: 'open',
  },
  {
    id: 1,
    value: TaskStatus.PENDING,
    text: 'Pending',
    verb: 'pending',
  },
  {
    id: 2,
    value: TaskStatus.ON_HOLD,
    text: 'On hold',
    verb: 'on hold',
  },
  {
    id: 3,
    value: TaskStatus.CLOSED,
    text: 'Closed',
    verb: 'close',
  },
  {
    id: 5,
    value: TaskStatus.CANCELLED,
    text: 'Canceled',
    verb: 'cancel',
  },
]

const StatusSelector = ({
  type,
  buttonLike,
  value,
  showLabel,
  onChange,
  isSimple,
  task,
  className,
  isRule = false,
  ...rest
}: {
  type?: 'meeting' | 'task' | null | undefined
  value: TaskStatus
  showLabel?: boolean
  buttonLike?: boolean
  onChange: StatusSelectorOnChange
  rest?: any
  style?: any
  isSimple?: boolean
  task?: DocumentData
  isRule?: boolean
  className?: string
}) => {
  const [searchValue, setSearchValue] = useState('')
  const authenticatedUser = useRecoilValue(authenticatedUserAtom)

  const isDisabled = useMemo(() => {
    if (isRule) return false
    const potentialDisabledStatuses = [
      TaskStatus.OPEN,
      TaskStatus.PENDING,
      TaskStatus.ON_HOLD,
    ]

    const canClose =
      (task && !task.associationId) ||
      isAllowed(
        authenticatedUser.selectedContact,
        authenticatedUser.acts,
        [task?.associationId] || [],
        ResourceType.PROPERTY,
        Feature.TASKS,
        Action.EDIT,
      ) ||
      (task?.createdBy &&
        task?.createdBy?.contactId === authenticatedUser.selectedContact.id) ||
      (task?.assignee &&
        task?.assignee?.contactId === authenticatedUser.selectedContact.id)

    return potentialDisabledStatuses.includes(value) && !canClose
  }, [
    task,
    authenticatedUser.selectedContact,
    value,
    isRule,
    authenticatedUser.acts,
  ])

  return (
    <span
      style={{ position: 'relative' }}
      // Stop event propogation.
      onClick={evt => {
        evt.stopPropagation()
        evt.preventDefault()
      }}
      className={className || ''}
    >
      <MultilevelDropdown
        position={Positions.Right}
        trigger={
          isRule ? undefined : buttonLike ? (
            <DropdownTriggerButton hasValue={!!value} {...rest} />
          ) : (
            <NullButton {...rest} />
          )
        }
        title={
          isRule ? (
            <FormDropdown width="215px">
              <FlexRow align="center" justify="space-between">
                <FlexRow align="center">
                  <Status type={type} status={value} isDisabled={isDisabled} />
                  <span style={{ fontWeight: 400, marginLeft: 10 }}>
                    {value != null
                      ? statusOptions.find(w => w.value === value)?.text
                      : ''}
                  </span>
                </FlexRow>

                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: 16, color: '#8A94A6' }}
                >
                  keyboard_arrow_down
                </span>
              </FlexRow>
            </FormDropdown>
          ) : (
            <FlexRow>
              <Status type={type} status={value} isDisabled={isDisabled} />
              {showLabel && (
                <span style={{ textTransform: 'capitalize', marginLeft: 8 }}>
                  {value}
                </span>
              )}
            </FlexRow>
          )
        }
        isDisabled={isDisabled}
      >
        {isSimple && (
          <MultilevelHeader
            onChange={setSearchValue}
            isDisabled={false}
            value={searchValue}
            placeholder="Change status"
            clearValue={() => setSearchValue('')}
          />
        )}
        {statusOptions.map(option => (
          <MultilevelItem
            key={option.id}
            onClick={() => {
              if (value === option.value) return
              onChange({
                task: task as SearchableTask,
                newStatus: option.value,
                action: TaskStatusChangeAction.SILENT,
              })
            }}
          >
            <FlexRow align="center">
              <Status
                status={option.value}
                style={{ marginRight: 8 }}
                isDisabled={isDisabled}
              />
              {/* TODO: what is isSimple? it looks identical to what's above */}
              {isSimple && (
                <Status
                  status={option.value}
                  style={{ marginRight: 8 }}
                  isDisabled={isDisabled}
                />
              )}
              {option.text}
            </FlexRow>
          </MultilevelItem>
        ))}
        {statusOptions.length === 0 && <MultilevelNoResults />}
      </MultilevelDropdown>
    </span>
  )
}

export default StatusSelector
